import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  Address,
  AddressField,
  Application,
  ApplicationAnswers,
  Field,
  FieldType,
  TextInputType,
} from '@bybeam/platform-types';
import { GenericFormData } from '@platform-ui-common/hooks/useForm/index.js';
import { getUnhousedKey } from '@platform-ui-common/utilities/address.js';
import { TemporaryExistingDocument } from '@platform-ui/@types/document.js';
import { evaluate } from '../ast/evaluate.js';
import { isMultiple } from './dropdown.js';

const KEY_SEPARATOR = '.';

// biome-ignore lint/complexity/noBannedTypes: {} is the actual return type
export function getDefaultValue(field: Field): {} | [] | false | '' {
  switch (field.type) {
    case FieldType.Calculated:
      return evaluate(field.formula, {});
    case FieldType.Typography:
      return {};
    case FieldType.Checkbox:
      return false;
    case FieldType.Complex:
    case FieldType.CheckboxGroup:
    case FieldType.Document:
      return [];
    case FieldType.Date:
    case FieldType.Text:
    case FieldType.Address:
      return '';
    case FieldType.Dropdown:
      return isMultiple(field) ? [] : '';
    // TODO: Default to empty string?
    case FieldType.RadioList:
      return undefined;
    default:
      return assertExhaustive(field);
  }
}

const ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'zip'];

// Given a field key, which is expected to be a nested path through the answers object,
// returns the nested value at that key. If the path is not valid or the answers has
// no value at that path yet, it returns undefined.
//
// For example, for key 'one.two' and answers { one: { two: { three: 'hello' } } },
// it would return { three: 'hello' }
export function getFieldAnswersValue<T>(key: string, answers: Partial<ApplicationAnswers>): T {
  if (!answers) {
    return;
  }
  return key.split(KEY_SEPARATOR).reduce((prev, curr) => prev?.[curr], answers);
}

export function getAddressAnswers<FormData extends GenericFormData>(
  field: AddressField,
  answers: Partial<ApplicationAnswers>,
  addresses: Address[],
): FormData {
  const { key, copyAddressKey, allowUnhoused } = field;
  const addressId = getFieldAnswersValue(`${key}Id`, answers);
  const address = addresses?.find((item: Address) => item.id === addressId);
  const copied =
    !!addressId &&
    !!copyAddressKey &&
    addressId === getFieldAnswersValue(`${copyAddressKey}Id`, answers);

  const unhousedKey = getUnhousedKey(key);

  return {
    ...Object.fromEntries(
      ADDRESS_FIELDS.map((fieldKey) => [`${key}.${fieldKey}`, address?.[fieldKey] ?? '']),
    ),
    ...(copyAddressKey && { [`${key}.copied`]: copied }),
    ...(allowUnhoused && { [unhousedKey]: !!getFieldAnswersValue(unhousedKey, answers) }),
  } as FormData;
}

export function parseSkippableDocumentAnswer(answer): string[] {
  if (Array.isArray(answer)) return answer;
  if (answer instanceof Object) {
    const { skipped, ...rest } = answer;
    return Object.values(rest);
  }
  return [];
}

function getDocumentAnswerPreview(answer, documents): TemporaryExistingDocument[] {
  return parseSkippableDocumentAnswer(answer).reduce((documentsToReturn, documentId) => {
    const document = documents.find((doc) => doc.id === documentId);
    if (document)
      documentsToReturn.push({
        id: document.id,
        name: document.filename,
        url: document.previewUrl,
        mimetype: document.mimetype,
      });
    return documentsToReturn;
  }, []);
}

export function getFieldInitialState<FormData extends GenericFormData>(
  field: Field,
  answers: Partial<ApplicationAnswers>,
  application: Application,
): FormData {
  const answer = getFieldAnswersValue(field.key, answers);

  switch (field.type) {
    case FieldType.Address:
      return getAddressAnswers(field, answers, application.addresses);
    case FieldType.Complex:
      return {
        [field.key]: ((answer ?? [{}]) as Array<unknown>).map((item) =>
          Object.fromEntries(
            field.subFields.flatMap((subField) =>
              Object.entries(getFieldInitialState(subField, item, application)),
            ),
          ),
        ),
      } as FormData;
    case FieldType.Calculated:
    case FieldType.Checkbox:
    case FieldType.CheckboxGroup:
    case FieldType.Date:
    case FieldType.Dropdown:
    case FieldType.RadioList:
      return {
        [field.key]: answer ?? getDefaultValue(field),
      } as FormData;
    case FieldType.Text: {
      let answerParsed = answer;
      if (answer && field.inputType === TextInputType.Currency) answerParsed = Number(answer) / 100;
      return { [field.key]: answerParsed ?? getDefaultValue(field) } as FormData;
    }
    case FieldType.Document:
      return {
        [field.key]:
          answer && application.documents?.length
            ? getDocumentAnswerPreview(answer, application.documents)
            : getDefaultValue(field),
      } as FormData;
    case FieldType.Typography:
      return {} as FormData;
    default:
      return assertExhaustive(field);
  }
}
